import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { dateToSqlDate } from '../../helpers/dateFuncs';
import fetchData from '../../helpers/fetchData';
import { formatNumber } from '../../helpers/numberUtils';
import { exportToExcel } from '../../helpers/exportFuncs';
import { FilterContext, IFilterState } from '../../contexts/FilterContext';
import { getComparator, Order, sortedRowInformation } from '../../helpers/sortingsFuncs';
import LoadingSpinner from '../LoadingSpinner';
import { visuallyHidden } from '@mui/utils';

interface HeaderMappingItem {
  name: string;
  type: string;
  digits: number;
  attach: string;
}

const detailsHeaderMapping: Record<string, HeaderMappingItem> = {
  network: {
    name: 'Netzwerk',
    type: 'string',
    digits: 0,
    attach: '',
  },
  leads: {
    name: 'Leads',
    type: 'number',
    digits: 0,
    attach: '',
  },
  total_cost: {
    name: 'Kosten',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  revenue_30d: {
    name: 'Revenue (30d)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  revenue_total: {
    name: 'Revenue (Total)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  anfo_bestaetigt_30: {
    name: 'Anfos 30d',
    type: 'number',
    digits: 0,
    attach: '',
  },
  avg_order_value: {
    name: '⌀ Abrechnungswert je Anfo (30 T.)',
    type: 'number',
    digits: 2,
    attach: '€',
  },
  conversion_rate: {
    name: 'Conversion Rate',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  roi30: {
    name: 'ROI (30d)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
  roi_total: {
    name: 'ROI (laufend)',
    type: 'number',
    digits: 2,
    attach: '%',
  },
};

type NetworkRoiData = {
  network: string;
  leads: number | null;
  total_cost: number | null;
  revenue_30d: number | null;
  revenue_total: number | null;
  anfo_bestaetigt_30: number | null;
  avg_order_value: number | null;
  conversion_rate: number | null;
  roi30: number | null;
  roi_total: number | null;
  [key: string]: any;
};

type Data = Record<DataKey, string>;
type DataKey = keyof NetworkRoiData;

const getData: (filterState: IFilterState) => Promise<NetworkRoiData[]> = async (
  filterState: IFilterState,
) => {
  if (!filterState.startDate || !filterState.endDate) {
    throw new Error('No start date or end date provided');
  }
  const networks = Array.isArray(filterState.network) ? filterState.network : [filterState.network];
  const themes = Array.isArray(filterState.theme)
    ? filterState.theme
    : filterState.theme
      ? [filterState.theme]
      : [];
  const options = {
    start_date: dateToSqlDate(filterState.startDate),
    end_date: dateToSqlDate(filterState.endDate),
    network: networks,
    ...(themes.length > 0 && { theme: themes }),
    ...(filterState.vnrVerlag.length > 0 && { verlag: filterState.vnrVerlag }),
  };

  const roiData = (await fetchData<Record<string, any>[]>(
    '/api/vnr/roi-per-network',
    options,
  )) as Record<string, any>[];

  return roiData.map((item) => ({
    network: item.network,
    leads: parseInt(item.leads) || 0,
    total_cost: parseFloat(item.total_cost) || 0,
    revenue_30d: parseFloat(item.revenue_30d) || 0,
    revenue_total: parseFloat(item.revenue_total) || 0,
    anfo_bestaetigt_30: parseInt(item.anfo_bestaetigt_30) || 0,
    avg_order_value: item.avg_order_value ? parseFloat(item.avg_order_value) : null,
    conversion_rate: item.conversion_rate ? parseFloat(item.conversion_rate) : null,
    roi30: item.roi30 ? parseFloat(item.roi30) : null,
    roi_total: item.roi_total ? parseFloat(item.roi_total) : null,
  }));
};

const computeSummary = (data: NetworkRoiData[]): NetworkRoiData => {
  if (!data.length) {
    return {
      network: 'Insgesamt',
      leads: 0,
      total_cost: 0,
      revenue_30d: 0,
      revenue_total: 0,
      anfo_bestaetigt_30: 0,
      avg_order_value: null,
      conversion_rate: null,
      roi30: 0,
      roi_total: 0,
    };
  }

  const totalLeads = data.reduce((acc, curr) => acc + (parseInt(String(curr.leads)) || 0), 0);
  const totalCost = data.reduce((acc, curr) => acc + (parseFloat(String(curr.total_cost)) || 0), 0);
  const totalRevenue30d = data.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.revenue_30d)) || 0),
    0,
  );
  const totalRevenueTotal = data.reduce(
    (acc, curr) => acc + (parseFloat(String(curr.revenue_total)) || 0),
    0,
  );
  const totalAnfos30d = data.reduce(
    (acc, curr) => acc + (parseInt(String(curr.anfo_bestaetigt_30)) || 0),
    0,
  );

  const roi30 = totalCost > 0 ? (totalRevenue30d / totalCost) * 100 : 0;
  const roiTotal = totalCost > 0 ? (totalRevenueTotal / totalCost) * 100 : 0;
  const avgOrderValue = totalAnfos30d > 0 ? totalRevenue30d / totalAnfos30d : null;
  const conversionRate = totalLeads > 0 ? (totalAnfos30d / totalLeads) * 100 : null;

  return {
    network: 'Insgesamt',
    leads: totalLeads,
    total_cost: totalCost,
    revenue_30d: totalRevenue30d,
    revenue_total: totalRevenueTotal,
    anfo_bestaetigt_30: totalAnfos30d,
    avg_order_value: avgOrderValue,
    conversion_rate: conversionRate,
    roi30: roi30,
    roi_total: roiTotal,
  };
};

export const RoiNetworkTable: React.FC = () => {
  const { filterState } = useContext(FilterContext)!;
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<DataKey>('leads');
  const [data, setData] = useState<NetworkRoiData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!filterState.startDate || !filterState.endDate) {
      return;
    }

    const selectedNetworks = Array.isArray(filterState.network)
      ? filterState.network
      : filterState.network
        ? [filterState.network]
        : [];

    // Only show network comparison when multiple networks are selected
    if (selectedNetworks.length <= 1) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const fetchData = async () => {
      try {
        const data = await getData(filterState);
        setData(data);
        setLoading(false);
      } catch (err: any) {
        setError(err);
        setLoading(false);
      }
    };

    fetchData();
  }, [
    filterState.startDate,
    filterState.endDate,
    filterState.network,
    filterState.theme,
    filterState.vnrVerlag,
  ]);

  if (!filterState.startDate || !filterState.endDate) {
    throw new Error('No start date or end date provided');
  }

  const selectedNetworks = Array.isArray(filterState.network)
    ? filterState.network
    : filterState.network
      ? [filterState.network]
      : [];

  if (selectedNetworks.length <= 1) {
    return null; // Don't render when only one or no networks selected
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  const headers = Object.keys(detailsHeaderMapping);

  const handleRequestSort = (property: keyof Data) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const createSortHandler = (property: keyof Data) => () => {
    handleRequestSort(property);
  };

  const sortedData = sortedRowInformation<NetworkRoiData>(data, getComparator(order, orderBy));

  return (
    <Box bgcolor="#fff">
      <Box>
        <Button
          onClick={() =>
            exportToExcel([...sortedData, computeSummary(data)], 'ROI-Netzwerk-Vergleich')
          }
          sx={{ float: 'right' }}
        >
          Download als Excel
        </Button>
      </Box>
      <Box>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header}>
                    <TableSortLabel
                      active={orderBy === header}
                      direction={orderBy === header ? order : 'asc'}
                      onClick={createSortHandler(header)}
                    >
                      {detailsHeaderMapping[header].name || header}
                      {orderBy === header ? (
                        <Box component="span" sx={{ ...visuallyHidden }}>
                          {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                    <TableCell key={key}>
                      {value.type === 'number'
                        ? row[key] === null
                          ? ''
                          : formatNumber(Number(row[key]), value.digits) + (value.attach || '')
                        : row[key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {/* Summary Row */}
              {(() => {
                const summary = computeSummary(sortedData);
                return (
                  <TableRow
                    sx={{
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      fontWeight: 'bold',
                    }}
                  >
                    {Object.entries(detailsHeaderMapping).map(([key, value]) => (
                      <TableCell key={key} sx={{ fontWeight: 'bold' }}>
                        {value.type === 'number'
                          ? summary[key] === null
                            ? ''
                            : formatNumber(Number(summary[key]), value.digits) +
                              (value.attach || '')
                          : summary[key]}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default RoiNetworkTable;
